import React, { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import ItemDetail from './ItemDetail';
import StarRating from './StarRating';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import type { Item, RentalPeriod, Category, Owner } from '../types';
import { formatGBP, gbpToPence } from '../utils/currency';
import './CategoryDetail.css';

interface FilterState {
  search: string;
  maxPrice: string;
  startDate: Date | null;
  endDate: Date | null;
  minRating: number;
}

// Mock data for owners
const mockOwners: Record<string, Owner> = {
  'john-smith': { id: 'john-smith', name: 'John Smith', rating: 4.5, totalRatings: 24 },
  'sarah-johnson': { id: 'sarah-johnson', name: 'Sarah Johnson', rating: 5, totalRatings: 31 },
  'mike-wilson': { id: 'mike-wilson', name: 'Mike Wilson', rating: 4, totalRatings: 15 },
  'emma-davis': { id: 'emma-davis', name: 'Emma Davis', rating: 4.8, totalRatings: 42 },
  'lisa-brown': { id: 'lisa-brown', name: 'Lisa Brown', rating: 4.2, totalRatings: 18 },
  'david-clark': { id: 'david-clark', name: 'David Clark', rating: 4.7, totalRatings: 28 },
  'amy-white': { id: 'amy-white', name: 'Amy White', rating: 4.9, totalRatings: 35 }
};

const CategoryDetail = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { categories, loading, getOwner } = useData();
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [owners, setOwners] = useState<Record<string, Owner>>({});
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    maxPrice: '',
    startDate: null,
    endDate: null,
    minRating: 0
  });

  // Get all items if categoryId is 'all', otherwise get items from specific category
  const categoryItems = useMemo(() => {
    if (categoryId === 'all') {
      return categories.reduce<Item[]>((allItems, category) => [...allItems, ...category.items], []);
    }
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.items : [];
  }, [categoryId, categories]);

  // Load owners for all items when categoryItems change
  useEffect(() => {
    const loadOwners = async () => {
      const ownerIds = Array.from(new Set(categoryItems.map(item => item.ownerId)));
      const ownerPromises = ownerIds.map(async (ownerId) => {
        const owner = await getOwner(ownerId);
        return { ownerId, owner };
      });
      
      const ownerResults = await Promise.all(ownerPromises);
      const ownersMap: Record<string, Owner> = {};
      
      ownerResults.forEach(({ ownerId, owner }) => {
        if (owner) {
          ownersMap[ownerId] = owner;
        } else {
          // Fallback to mock owner if not found
          ownersMap[ownerId] = mockOwners[ownerId] || { 
            id: ownerId, 
            name: 'Unknown Owner', 
            rating: 0, 
            totalRatings: 0 
          };
        }
      });
      
      setOwners(ownersMap);
    };

    if (categoryItems.length > 0) {
      loadOwners();
    }
  }, [categoryItems, getOwner]);

  const handleFilterChange = (key: keyof FilterState, value: string | number | Date | null) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    handleFilterChange('startDate', start);
    handleFilterChange('endDate', end);
  };

  const handleClearAvailability = () => {
    handleFilterChange('startDate', null);
    handleFilterChange('endDate', null);
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      maxPrice: '',
      startDate: null,
      endDate: null,
      minRating: 0
    });
  };

  const isItemAvailable = (item: Item, start: Date, end: Date): boolean => {
    if (!item.rentalPeriods || item.rentalPeriods.length === 0) return true;
    
    return !item.rentalPeriods.some((period: RentalPeriod) => {
      const periodStart = new Date(period.startDate);
      const periodEnd = new Date(period.endDate);
      
      // Check if there's any overlap between the periods
      return (
        (start <= periodEnd && start >= periodStart) ||
        (end <= periodEnd && end >= periodStart) ||
        (start <= periodStart && end >= periodEnd)
      );
    });
  };

  const filteredItems = useMemo(() => {
    return categoryItems.filter(item => {
      // Search filter
      const searchTerm = filters.search.toLowerCase();
      const matchesSearch = 
        item.name.toLowerCase().includes(searchTerm) ||
        item.description.toLowerCase().includes(searchTerm);
      if (!matchesSearch) return false;

      // Price filter
      const maxPrice = filters.maxPrice ? parseFloat(filters.maxPrice) : Infinity;
      if (item.dailyRateGBP > maxPrice) return false;

      // Availability filter
      if (filters.startDate && filters.endDate) {
        if (!isItemAvailable(item, filters.startDate, filters.endDate)) return false;
      }

      // Rating filter
      if (filters.minRating > 0) {
        const owner = owners[item.ownerId];
        if (!owner || owner.rating < filters.minRating) return false;
      }

      return true;
    });
  }, [categoryItems, filters, owners]);

  if (loading) {
    return (
      <div className="category-detail">
        <h2>Loading...</h2>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Fetching items from database...
        </div>
      </div>
    );
  }

  if (categoryId !== 'all' && !categories.find(cat => cat.id === categoryId)) {
    return (
      <div className="category-detail">
        <h2>Category not found</h2>
      </div>
    );
  }

  const selectedItemData = categoryItems.find(item => item.id === selectedItem);

  return (
    <div className="category-detail">
      <h2>{categoryId === 'all' ? 'All Items' : categories.find(cat => cat.id === categoryId)?.name}</h2>

      <div className="filters-section">
        <button 
          className={`filters-toggle ${isFiltersVisible ? 'active' : ''}`}
          onClick={() => setIsFiltersVisible(!isFiltersVisible)}
        >
          <span>Filters</span>
          <span className="toggle-icon">{isFiltersVisible ? '−' : '+'}</span>
        </button>

        <div className={`filters-content ${isFiltersVisible ? 'expanded' : ''}`}>
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search items..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>

          <div className="filters-container">
            <div className="filter-group">
              <label>Maximum Price (per day)</label>
              <div className="price-inputs">
                <div className="input-with-prefix">
                  <span className="prefix">£</span>
                  <input
                    type="number"
                    placeholder="Max price"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    min="0"
                    step="0.01"
                    className="price-input"
                  />
                </div>
              </div>
            </div>

            <div className="filter-group">
              <label>Available Between</label>
              <div className="date-picker-container">
                <div className="date-picker-wrapper">
                  <DatePicker
                    selected={filters.startDate}
                    onChange={handleDateChange}
                    startDate={filters.startDate}
                    endDate={filters.endDate}
                    selectsRange
                    minDate={new Date()}
                    placeholderText="Select dates..."
                    className="date-picker-input"
                    monthsShown={1}
                    showMonthDropdown={false}
                    showYearDropdown={false}
                    previousMonthButtonLabel="←"
                    nextMonthButtonLabel="→"
                  />
                  {(filters.startDate || filters.endDate) && (
                    <button 
                      className="clear-filter-btn"
                      onClick={handleClearAvailability}
                      title="Clear dates"
                      aria-label="Clear date filter"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="filter-group">
              <label>Minimum Rating</label>
              <div className="rating-filter">
                <select
                  value={filters.minRating}
                  onChange={(e) => handleFilterChange('minRating', parseFloat(e.target.value))}
                  className="rating-select"
                >
                  <option value="0">Any rating</option>
                  <option value="3">3+ stars</option>
                  <option value="3.5">3.5+ stars</option>
                  <option value="4">4+ stars</option>
                  <option value="4.5">4.5+ stars</option>
                  <option value="5">5 stars only</option>
                </select>
              </div>
            </div>
          </div>

          <div className="filters-footer">
            <button 
              className="reset-filters-btn"
              onClick={handleResetFilters}
              aria-label="Reset all filters"
            >
              Reset All Filters
            </button>
          </div>
        </div>
      </div>

      <div className="items-grid">
        {filteredItems.map(item => (
          <div 
            key={item.id} 
            className="item-card"
            onClick={() => setSelectedItem(item.id)}
          >
            <div className="item-thumbnail">
              <img 
                src={item.images[0] || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4='}
                alt={item.name}
                className="item-image"
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const img = e.currentTarget;
                  if (img.src !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=') {
                    img.onerror = null; // Prevent infinite loop
                    img.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNODAgNjBIMTIwVjE0MEg4MFY2MFoiIGZpbGw9IiNEREQiLz48cGF0aCBkPSJNNjUgOTBIMTM1VjExMEg2NVY5MFoiIGZpbGw9IiNEREQiLz48L3N2Zz4=';
                  }
                }}
              />
            </div>
            <div className="item-content">
              <div className="item-header">
                <h3>{item.name}</h3>
              </div>
              <p className="item-description">{item.description}</p>
              <div className="item-footer">
                <div className="owner-info">
                  <span className="owner">{owners[item.ownerId]?.name || 'Loading...'}</span>
                  <StarRating 
                    rating={owners[item.ownerId]?.rating || 0}
                    totalRatings={owners[item.ownerId]?.totalRatings || 0}
                    size="small"
                    showCount={false}
                  />
                </div>
                <div className="price-container">
                  <span className="daily-rate">£{item.dailyRateGBP.toFixed(2)}/day</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <div className="no-results">
          <p>No items found matching your criteria</p>
        </div>
      )}

      {selectedItemData && (
        <ItemDetail
          item={selectedItemData}
          onClose={() => setSelectedItem(null)}
        />
      )}
    </div>
  );
};

export default CategoryDetail; 